import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Input, Row, Layout, Button } from '@iqueue/ui-kit'
import PickNode from '../../components/picker/PickNode'
import { assoc, update, append, remove, path } from 'ramda'
import PickSensorType from '../../components/picker/PickSensorType'
import PickSensorModel from '../../components/picker/PickSensorModel'
import PickSensorStatus from '../../components/picker/PickSensorStatus'
import { t } from '../../hooks/useTranslate'

function RangeInput({
  min,
  max,
  minTitle = 'Min',
  maxTitle = 'Max',
  name,
  value,
  flat = false,
  strict,
  children,
  onChange,
  onDelete,
  auxiliary,
}) {
  const handle = useRef(false)
  const [minMax, setMinMax] = useState({})

  const handleValueChange = useCallback((v, name) => {
    handle.current = true
    setMinMax(assoc((name || '').includes('min') ? 'min' : 'max', v))
  }, [])

  const checkValidity = useCallback(() => {
    if (!strict) return true

    if (min !== undefined && min !== null && !isNaN(min) && minMax.min > min) {
      return false
    }

    if (max !== undefined && max !== null && !isNaN(max) && minMax.max < max) {
      return false
    }

    return minMax.min <= minMax.max
  }, [minMax, min, max, strict])

  useEffect(() => {
    if (!value) return

    if (flat) {
      if (!value) return

      setMinMax({
        min: value[0],
        max: value[1],
      })
      return
    }

    setMinMax(value)
  }, [flat, value])

  useEffect(() => {
    if (!handle.current) return
    handle.current = false

    if (isNaN(minMax.min) || isNaN(minMax.max) || minMax.max < minMax.min) {
      // skipping invalid values
      return
    }

    if (typeof onChange === 'function') {
      if (flat) {
        onChange([minMax.min, minMax.max])
      } else {
        onChange(minMax)
      }
    }
  }, [flat, minMax, onChange])

  return <>
    { flat && !auxiliary && <Input
      hidden
      type={ 'raw' }
      name={ name }
      serialize={ JSON.stringify.bind(null) }
      deserialize={ JSON.parse.bind(null) }
      value={ [minMax.min, minMax.max] }
    /> }

    <Layout horizontal>
      <Input
        required
        min={ min }
        max={ max }
        type={ 'number' }
        auxiliary={ flat }
        value={ minMax.min }
        placeholder={ minTitle }
        onChange={ handleValueChange }
        checkValidity={ checkValidity }
        name={ flat ? 'min' : `${ name }.min` }
      />

      <Input
        required
        min={ min }
        max={ max }
        type={ 'number' }
        auxiliary={ flat }
        value={ minMax.max }
        placeholder={ maxTitle }
        onChange={ handleValueChange }
        checkValidity={ checkValidity }
        name={ flat ? 'max' : `${ name }.max` }
      />

      { children }

      { !!onDelete && <Button
        danger
        icon={ 'delete' }
        onClick={ onDelete }
        style={ {
          marginTop: '2rem',
          height: '3rem',
          width: '3rem',
          padding: '0rem',
          border: 'none',
        } }
      /> }
    </Layout>
  </>
}

function ThresholdsInput({
  name = 'thresholds',
  value = [],
  minTitle,
  maxTitle,
  placeholder,
}) {
  const [rawValue, setRawValue] = useState([])

  useEffect(() => {
    if (!value) return
    setRawValue(value)
  }, [])

  return <>
    { !!placeholder && <h5>{ placeholder }</h5> }

    <Input
      hidden
      type={ 'raw' }
      name={ name }
      serialize={ JSON.stringify.bind(null) }
      deserialize={ JSON.parse.bind(null) }
      value={ rawValue }
    />

    { rawValue.map((val, i) => <RangeInput
      flat
      strict
      auxiliary
      key={ i }
      value={ val }
      minTitle={ minTitle }
      maxTitle={ maxTitle }
      onChange={ (v) => setRawValue(update(i, v)) }
      onDelete={ () => setRawValue(remove(i, 1)) }
    />) }

    <RangeInput
      flat
      auxiliary
      key={ rawValue.length }
      minTitle={ minTitle }
      maxTitle={ maxTitle }
      onChange={ (v) => setRawValue(append(v)) }
    />
  </>
}

export default function SensorUpsertForm({ model }) {
  return Object.keys(model).length !== 0 ? <>
    <Input name={ 'id' } hidden value={ model.id }/>

    <Row>
      {/* TODO HEX EDITOR! */ }
      <Input
        autoFocus
        size={ 12 }
        name={ 'devEUI' }
        placeholder={ t('sensor.form.devEUI') }
        value={ model?.devEUI }
      />
    </Row>

    <Row>
      <PickSensorType
        required
        size={ 6 }
        placeholder={ t('sensor.form.type') }
        value={ model?.type }
      />

      <PickSensorStatus
        required
        size={ 6 }
        disabled={ !!model?.id }
        placeholder={ t('sensor.form.status') }
        value={ model.status }
      />
    </Row>

    <Row>
      <PickSensorModel
        required
        size={ 12 }
        name={ 'modelId' }
        placeholder={ t('sensor.form.model') }
        value={ model?.modelId }
      />
    </Row>

    <Row>
      <PickNode
        required
        size={ 12 }
        name={ 'nodeId' }
        current={ model }
        placeholder={ t('sensor.form.node') }
        value={ model?.nodeId }
      />
    </Row>

    <Row>
      <Input
        required
        size={ 12 }
        name={ 'serialNumber' }
        placeholder={ t('sensor.form.serialNumber') }
        value={ model?.serialNumber }
      />
    </Row>

    <RangeInput
      name={ 'limits' }
      minTitle={ t('sensor.rangeInput.minValue') }
      maxTitle={ t('sensor.rangeInput.maxValue') }
      value={ model?.limits }
    />

    <ThresholdsInput
      placeholder={ t('sensor.thresholds.warning') }
      name={ 'thresholds.warn' }
      value={ path(['thresholds', 'warn'], model) }
      minTitle={ t('sensor.thresholds.from') }
      maxTitle={ t('sensor.thresholds.to') }
    />

    <ThresholdsInput
      placeholder={ t('sensor.thresholds.critical') }
      name="thresholds.critical"
      value={ path(['thresholds', 'critical'], model) }
      minTitle={ t('sensor.thresholds.from') }
      maxTitle={ t('sensor.thresholds.to') }
    />
  </> : <>
  </>
}
